/**
 * How a humanoid is drawn, apart from what it is (proportions) and what it wears (look).
 *
 * `lpc` is the style fitted to sit beside LPC art: its raster, its sampled skins, and the muscle
 * marks painted on the LPC-fitted body. `realist` is Ember's own, for bodies at real human
 * proportions: picked skins with cool shadows and a plum outline, one key light cut into clean
 * steps instead of LPC's rim of shade inside every outline, and no LPC marks (they are calibrated to the
 * LPC body and would land in the wrong places on any other).
 */
import { EmberClip, NATURAL_CLIPS } from "./clips"
import { Material, PAINTED_SKIN, SKIN } from "./palette"
import { DEFAULT_RASTER, RasterOptions } from "./raster"

export interface EmberStyle {
    raster: RasterOptions
    skins: Record<string, Material>
    /** Paint LPC's muscle marks (marks.ts). */
    marks: boolean
    /** The eye style (face.ts) for a look that names none. */
    eyes?: string
    /** Clips drawn this style's own way, by clip name, over the shared EMBER_CLIPS. */
    clips?: Record<string, EmberClip>
}

export const EMBER_STYLES: Record<string, EmberStyle> = {
    lpc: { raster: DEFAULT_RASTER, skins: SKIN, marks: true },
    realist: {
        raster: {
            ...DEFAULT_RASTER,
            outline: "dark",
            // Chest, waist and hips are one body: no seam drawn where one swells past the next.
            creaseDepth: 3,
            // Seams only where a limb segment clearly overlaps the next, not at every knee.
            jointLine: 2.5,
            // One key light from above-left, cut into steps at fixed darkness: a clean terminator
            // running down the whole body, rather than LPC's share of every step per region and its
            // rim of shade inside every outline.
            shading: {
                ...DEFAULT_RASTER.shading,
                light: 1,
                facing: 0.1,
                rim: 0,
                thresholds: [0.04, 0.24, 0.55],
            },
        },
        skins: PAINTED_SKIN,
        marks: false,
        eyes: "dot",
        // A real body walks and runs like one: LPC's gait, scaled to its long legs, shuffles.
        clips: NATURAL_CLIPS,
    },
}

export const DEFAULT_STYLE = "lpc"
